/* eslint-disable semi */
/* eslint-disable comma-dangle */
// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Fragment } from 'react'
import { Slide, toast } from 'react-toastify'
import baseurl from '../../../@core/auth/jwt/baseurl'
import emiter from '../../../utility/hooks/emiiter'

const showErrorMsg = (error) => {
  toast.error(
    <Fragment>
      <div className="toastify-header">
        <div className="title-wrapper">
          <h6 className="toast-title fw-bold">Error</h6>
        </div>
      </div>
      <div className="toastify-body">
        <span>{error}</span>
      </div>
    </Fragment>,
    {
      icon: false,
      transition: Slide,
      hideProgressBar: false,
      autoClose: 2000
    }
  )
}

const showSuccessMsg = (msg) => {
  toast.success(
    <Fragment>
      <div className="toastify-header">
        <div className="title-wrapper">
          <h6 className="toast-title fw-bold">Success</h6>
        </div>
      </div>
      <div className="toastify-body">
        <span>{msg}</span>
      </div>
    </Fragment>,
    {
      icon: false,
      transition: Slide,
      hideProgressBar: false,
      autoClose: 2000
    }
  )
}

export const getData = createAsyncThunk(
  'appManufacturingSiteOldData/getData',
  async (params) => {
    const response = await baseurl.get('/api/app/manufacturing-site-old-data', {
      params
    })
    return {
      params,
      data: response.data.items,
      total: response.data.totalCount
    }
  },
  {
    dispatchConditionRejection: true
  }
)

export const getById = createAsyncThunk(
  'appManufacturingSiteOldData/getById',
  async (id) => {
    try {
      const response = await baseurl.get(`/api/app/manufacturing-site-old-data/${id}`, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (response.status === 200) {
        return response.data
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return rejectWithValue(response.data)
    } catch (error) {
      return rejectWithValue(error)
    }
  },
  {
    dispatchConditionRejection: true
  }
)

export const addManufacturingSiteOldData = createAsyncThunk(
  'appManufacturingSiteOldData/addManufacturingSiteOldData',
  async (dosageform, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.post('/api/app/manufacturing-site-old-data', dosageform, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 200) {
        showSuccessMsg('Added Successfully')
        emiter.emit('close-dosageform-add-or-edit-popup')
        await dispatch(getData(getState().dosageform.params))
        await dispatch(getById(res.data.id))
        return dosageform
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const updateManufacturingSiteOldData = createAsyncThunk(
  'appManufacturingSiteOldData/updateManufacturingSiteOldData',
  async (dosageform, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.put(`/api/app/manufacturing-site-old-data/${dosageform.id}`, dosageform, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 200) {
        showSuccessMsg('Updated Successfully')
        await dispatch(getData(getState().dosageform.params))
        await dispatch(getById(dosageform.id))
        return dosageform
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deleteManufacturingSiteOldData = createAsyncThunk(
  'appManufacturingSiteOldData/deleteManufacturingSiteOldData',
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.delete(`/api/app/manufacturing-site-old-data/${id}`, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 204) {
        emiter.emit('close-dosageform-delete-popup')
        await dispatch(getData(getState().manufacturingSiteOldData.params))
        return id
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const initManufacturingSiteOldData = createAsyncThunk(
  'appManufacturingSiteOldData/initManufacturingSiteOldData',
  (loading) => loading,
  {}
)

export const loadManufacturingSiteOldDatatLookups = createAsyncThunk(
  'appManufacturingSiteOldData/loadManufacturingSiteOldDatatLookups',
  async () => {
    const productionLineActivitiesResponse = await baseurl.get('/api/app/production-line-activity', {
      params: {
        MaxResultCount: 1000
      }
    })

    return {
      productionLineActivities: productionLineActivitiesResponse.data?.items
    }
  },
  {}
)

export const appManufacturingSiteOldDataSlice = createSlice({
  name: 'appManufacturingSiteOldData',
  initialState: {
    data: [],
    total: 1,
    params: {},
    allData: [],
    selectedManufacturingSiteOldData: null,
    loading: true,
    getByIdLoading: false,
    saveLoading: false
  },
  reducers: {
    setUploads: (state, action) => {
      state.selectedManufacturingSiteOldData = {
        ...state.selectedManufacturingSiteOldData,
        uploads: action.payload
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getData.pending, (state) => {
        state.loading = true
      })
      .addCase(getData.rejected, (state) => {
        state.loading = false
      })
      .addCase(getData.fulfilled, (state, action) => {
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.total
        state.loading = false
      })

      .addCase(getById.pending, (state) => {
        state.getByIdLoading = true
        state.selectedManufacturingSiteOldData = null
      })
      .addCase(getById.rejected, (state) => {
        state.getByIdLoading = false
      })
      .addCase(getById.fulfilled, (state, action) => {
        state.getByIdLoading = false
        state.selectedManufacturingSiteOldData = action.payload
      })

      .addCase(addManufacturingSiteOldData.pending, (state) => {
        state.saveLoading = true
      })
      .addCase(addManufacturingSiteOldData.rejected, (state) => {
        state.saveLoading = false
      })
      .addCase(addManufacturingSiteOldData.fulfilled, (state) => {
        state.saveLoading = false
      })

      .addCase(updateManufacturingSiteOldData.pending, (state) => {
        state.saveLoading = true
      })
      .addCase(updateManufacturingSiteOldData.rejected, (state) => {
        state.saveLoading = false
      })
      .addCase(updateManufacturingSiteOldData.fulfilled, (state) => {
        state.saveLoading = false
      })

      .addCase(deleteManufacturingSiteOldData.pending, (state) => {
        state.deleteLoading = true
      })
      .addCase(deleteManufacturingSiteOldData.rejected, (state) => {
        state.deleteLoading = false
      })
      .addCase(deleteManufacturingSiteOldData.fulfilled, (state) => {
        state.deleteLoading = false
      })
      .addCase(initManufacturingSiteOldData.fulfilled, (state) => {
        state.selectedManufacturingSiteOldData = null
      })

    builder.addCase(loadManufacturingSiteOldDatatLookups.fulfilled, (state, action) => {
      state.productionLineActivities = action.payload.productionLineActivities
    })
  }
})
export const { setUploads } = appManufacturingSiteOldDataSlice.actions
export default appManufacturingSiteOldDataSlice.reducer
